import React, { useState, useEffect, useRef, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Layout from '@hobs/components/CoreLayout';
import classNames from 'classnames';
import { Popover, Position } from '@blueprintjs/core';
import Header from '@hobs/components/Header';
import Icon from '@hobs/components/Icon';
import Submenu from '@hobs/components/Submenu';
import Tooltip from '@hobs/components/Tooltip';
import WithRoles from '@hobs/hocs/WithRoles';
import { USER_ROLES, SN_MANUAL_FILE_NAME } from '@hobs/utils/constants';
import { Notifications } from '@hobs/components/Notifications/Notifications';
import { getNotifications } from '@hobs/actions/notifications';
import { Download } from '@hobs/utils/download';
import { Scrollbars } from 'react-custom-scrollbars';
import { withTranslation } from 'react-i18next/src/index';
import { getPhaseTypes } from '@hobs/actions/phases';
import { getUserManual } from '@hobs/actions/userManual';
import { userContract } from '../../propTypes/user';
import {
  changeCurrentExercise,
  getRemainingDaysObject
} from '../../actions/exercises';

import { UserMenu } from './UserMenu/UserMenu';
import '@blueprintjs/core/src/blueprint.scss';
import './PrivateLayout.scss';
import SettingsMenu from './SettingsMenu';

const submenuLinks = {
  links: [
    {
      link: '/',
      name: 'Home'
    },
    {
      link: '/terms-and-conditions-view',
      name: 'Terms and Conditions'
    },
    {
      link: '/catalog',
      name: 'Catalog'
    },
    {
      link: '/sor-requisites',
      name: 'SOR requisites'
    },
    {
      link: '/requested-services',
      name: 'Requested services'
    },
    {
      link: '/tickets',
      name: 'Tickets list'
    },
    {
      link: '/meal-planner',
      name: 'Meal planning'
    }
  ]
};

const PrivateLayout = ({
  children,
  exercises,
  remainingDaysObject,
  user,
  changeCurrentExercise,
  getRemainingDaysObject,
  getPhaseTypes,
  phaseTypes,
  t,
  history,
  withSubmenu = true,
  hasUserPermission,
  getUserManual,
  notifications,
  getNotifications
}) => {
  const [currentExerciseId, setCurrentExerciseId] = useState(
    user.CurrentExerciseId
  );
  const [
    userSettingsTooltipIsDisabled,
    setUserSettingsTooltipIsDisabled
  ] = useState(false);

  const exercisePopoverRef = useRef(null);
  const settingsPopoverRef = useRef(null);

  useEffect(() => {
    getRemainingDaysObject();
    getPhaseTypes();
    queryNotifications({ skip: 0, take: 0 });
  }, [getPhaseTypes, getRemainingDaysObject, queryNotifications]);

  useEffect(() => {
    const unlisten = history.listen(() => {
      if (history.location.pathname !== '/requested-services') {
        queryNotifications({ skip: 0, take: 0 });
      }
    });

    return unlisten();
  }, [history, queryNotifications]);

  const changeCurrentExerciseId = id => () => {
    changeCurrentExercise(id).then(response => {
      if (response.success) {
        setCurrentExerciseId(id);
        getRemainingDaysObject();
        queryNotifications(0, 0);
        if (exercisePopoverRef && exercisePopoverRef.current) {
          exercisePopoverRef.current.setOpenState(false);
        }
        history.push('/');
      }
    });
  };

  const queryNotifications = useCallback(
    filter => {
      const notificationsQueryFilterItem = localStorage.getItem(
        'NotificationsQueryFilter'
      );

      if (notificationsQueryFilterItem == null) {
        getNotifications(filter);
        return;
      }

      const notificationsQueryFilter = JSON.parse(notificationsQueryFilterItem);

      getNotifications({ ...notificationsQueryFilter, ...filter });
    },
    [getNotifications]
  );

  const downloadUserManual = async event => {
    event.preventDefault();
    const userManual = await getUserManual();

    if (userManual.success) {
      Download(userManual.payload, SN_MANUAL_FILE_NAME);
    }
  };

  const renderExercises = () => {
    const currentExercise = exercises.find(
      exercise => exercise.exerciseId === currentExerciseId
    );

    return (
      <Popover
        ref={exercisePopoverRef}
        className="header--exercises"
        minimal={true}
        usePortal={true}
        modifiers={{
          offset: {
            offset: '-10, 18'
          }
        }}
        content={
          <div className="header--exercises-menu">
            <Scrollbars
              style={{ width: '100%' }}
              renderThumbVertical={({ style, ...props }) => (
                <div
                  className={classNames('vertical-thumb')}
                  {...props}
                  style={{ ...style }}
                />
              )}
              autoHeight={true}
            >
              {exercises.map(item => {
                return (
                  <div
                    className={classNames('header--exercises-item', {
                      active: item.exerciseId === currentExerciseId
                    })}
                    key={item.exerciseId}
                    onClick={changeCurrentExerciseId(item.exerciseId)}
                  >
                    {item.name}
                  </div>
                );
              })}
            </Scrollbars>
          </div>
        }
        position={Position.BOTTOM_LEFT}
      >
        <div>{currentExercise ? currentExercise.name : ''}</div>
      </Popover>
    );
  };

  const onNotificationSelected = notification => {
    history.push(`/service-request/${notification.ServiceRequestId}`);
  };

  return (
    <>
      <Header
        left={renderExercises()}
        right={
          <>
            {(hasUserPermission(USER_ROLES.ADMIN) ||
              hasUserPermission(USER_ROLES.USER)) && (
              <Notifications
                notificationCount={notifications.totalCount}
                data={notifications.data}
                onLoadMore={getNotifications}
                onSelected={onNotificationSelected}
              />
            )}
            <a href="/" onClick={downloadUserManual} className="user-manual">
              <Icon icon="UserManual" /> {t('User manual')}
            </a>
            <Tooltip
              content={user.PartyName}
              disabled={userSettingsTooltipIsDisabled}
            >
              <Popover
                onOpening={() => setUserSettingsTooltipIsDisabled(true)}
                onClosed={() => setUserSettingsTooltipIsDisabled(false)}
                className={classNames([
                  'header--controls',
                  'header--user-menu'
                ])}
                minimal={true}
                usePortal={false}
                modifiers={{
                  offset: {
                    offset: '-10, 18'
                  }
                }}
                content={<UserMenu t={t} />}
                position={Position.BOTTOM_LEFT}
              >
                <>{user.UserName}</>
              </Popover>
            </Tooltip>
            {hasUserPermission(USER_ROLES.ADMIN) && (
              <Popover
                ref={settingsPopoverRef}
                className={classNames([
                  'header--controls',
                  'header--settings-menu'
                ])}
                minimal={true}
                usePortal={false}
                modifiers={{
                  offset: {
                    offset: '-10, 18'
                  }
                }}
                content={
                  <SettingsMenu
                    t={t}
                    onOptionClick={() => {
                      settingsPopoverRef.current.setOpenState(false);
                    }}
                  />
                }
                position={Position.BOTTOM_LEFT}
              >
                <>
                  <Icon icon="settings" /> {t('Settings')}
                </>
              </Popover>
            )}
          </>
        }
        submenu={
          withSubmenu && (
            <Submenu
              left={submenuLinks.links}
              right={{ phaseTypes, ...remainingDaysObject }}
            />
          )
        }
      />
      <Layout>{children}</Layout>
    </>
  );
};

PrivateLayout.propTypes = {
  user: userContract,
  exercises: PropTypes.array,
  history: PropTypes.object,
  withSubmenu: PropTypes.bool,
  remainingDaysObject: PropTypes.object,
  changeCurrentExercise: PropTypes.func,
  getRemainingDaysObject: PropTypes.func,
  getUserManual: PropTypes.func,
  phaseTypes: PropTypes.array,
  getPhaseTypes: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.array,
    PropTypes.element
  ]),
  t: PropTypes.func,
  hasUserPermission: PropTypes.func,
  getNotifications: PropTypes.func,
  notifications: PropTypes.object
};

const mapStateToProps = state => ({
  user: state.auth.user,
  exercises: state.exercises.list,
  remainingDaysObject: state.exercises.remainingDayObject,
  phaseTypes: state.phases.phaseTypes,
  notifications: state.notifications
});

const mapDispatchToProps = {
  changeCurrentExercise,
  getRemainingDaysObject,
  getPhaseTypes,
  getUserManual,
  getNotifications
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withRouter(WithRoles(PrivateLayout))));
